import { useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useEventListener } from 'usehooks-ts';
import type { QuickAddProduct } from './types';

function clampQuantity(value: string, max: number): string {
  const quantity = parseFloat(value);

  if (quantity < 1) return '';
  if (quantity > max) return String(max);

  return value;
}

interface QuickAddFormProps {
  action: string;
  product: QuickAddProduct;
  quantityId: string;
  onAdded: () => void;
}

export default function QuickAddForm({
  action,
  product,
  quantityId,
  onAdded,
}: QuickAddFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  useEventListener('ajax:success', onAdded, formRef);

  useEffect(() => {
    if (product.quantityLock) return;

    // Auto-select when the primary pointer supports precise input and hover.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches)
      quantityRef.current?.select();
  }, [product.quantityLock]);

  function handleQuantityChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    input.value = clampQuantity(input.value, product.maxQuantity);
  }

  return (
    <form ref={formRef} action={action} method="post" data-remote="true">
      <input type="hidden" name="item[product_id]" value={product.id} />
      <div className="bio-quick-add__quantity" hidden={product.quantityLock}>
        <label htmlFor={quantityId}>Quantity:</label>
        <input
          ref={quantityRef}
          id={quantityId}
          type="number"
          name="item[quantity]"
          defaultValue={1}
          min={1}
          max={product.maxQuantity}
          onChange={handleQuantityChange}
          step={1}
          required
        />
      </div>
      {product.repeatsRemaining !== null && (
        <p className="bio-quick-add__repeats">
          Repeats remaining: <b>{product.repeatsRemaining}</b>
        </p>
      )}
      <button
        type="submit"
        className="bio-quick-add__submit"
        data-disable-with="Add"
      >
        Add
      </button>
    </form>
  );
}
